/**
 * SessionTimeoutWarning Component
 *
 * This component displays a warning to the user when their session is about to expire
 * due to inactivity. It provides options to continue the session or log out.
 */

import { useEffect, useMemo } from 'react';
import Button from '@/components/ui/Button';

export interface SessionTimeoutWarningProps {
  secondsUntilTimeout: number;
  isActive?: boolean;
  /**
   * Total seconds in the warning window (used to draw the countdown ring).
   * Defaults to 60 (matches the 1-minute warning in useSessionTimeoutOptimized).
   */
  warningWindowSeconds?: number;
  onContinue: () => void;
  onLogout: () => void;
}

// SVG ring geometry constants
const RING_SIZE = 56;
const RING_STROKE = 5;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRC = 2 * Math.PI * RING_RADIUS;

const SessionTimeoutWarning = ({
  secondsUntilTimeout,
  isActive = true,
  warningWindowSeconds = 60,
  onContinue,
  onLogout
}: SessionTimeoutWarningProps) => {
  // Focus the continue button when the warning appears
  useEffect(() => {
    const continueButton = document.getElementById('session-continue-button');
    if (continueButton) {
      continueButton.focus();
    }
  }, []);

  // Format the time remaining
  const formatTimeRemaining = () => {
    if (secondsUntilTimeout < 60) {
      return `${secondsUntilTimeout} second${secondsUntilTimeout !== 1 ? 's' : ''}`;
    }
    const minutes = Math.floor(secondsUntilTimeout / 60);
    const seconds = secondsUntilTimeout % 60;
    return `${minutes} minute${minutes !== 1 ? 's' : ''} and ${seconds} second${seconds !== 1 ? 's' : ''}`;
  };

  // Ring fill: full at warningWindowSeconds, empty at 0.
  const { dashOffset, ringColorClass } = useMemo(() => {
    const window = Math.max(1, warningWindowSeconds);
    const clamped = Math.max(0, Math.min(secondsUntilTimeout, window));
    const fraction = clamped / window;
    // strokeDashoffset of 0 = full ring, RING_CIRC = empty.
    const offset = RING_CIRC * (1 - fraction);
    // Switch to red at < 25% remaining
    const colorClass = fraction < 0.25
      ? 'text-red-500 dark:text-red-400'
      : fraction < 0.5
        ? 'text-orange-500 dark:text-orange-400'
        : 'text-amber-500 dark:text-amber-400';
    return { dashOffset: offset, ringColorClass: colorClass };
  }, [secondsUntilTimeout, warningWindowSeconds]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-lg max-w-md w-full mx-4">
        <div className="flex items-center mb-4">
          <div className="relative mr-3 flex items-center justify-center" style={{ width: RING_SIZE, height: RING_SIZE }}>
            <svg
              width={RING_SIZE}
              height={RING_SIZE}
              viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
              className="-rotate-90"
              aria-hidden="true"
            >
              <circle
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                fill="none"
                strokeWidth={RING_STROKE}
                className="text-slate-200 dark:text-slate-700"
                stroke="currentColor"
              />
              <circle
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                fill="none"
                strokeWidth={RING_STROKE}
                strokeLinecap="round"
                stroke="currentColor"
                className={`${ringColorClass} transition-[stroke-dashoffset,color] duration-1000 ease-linear`}
                strokeDasharray={RING_CIRC}
                strokeDashoffset={dashOffset}
              />
            </svg>
            <span
              className={`absolute text-sm font-semibold tabular-nums ${ringColorClass}`}
              aria-live="polite"
            >
              {Math.max(0, secondsUntilTimeout)}
            </span>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
            Session timeout warning
          </h2>
        </div>

        <div className="mb-6">
          <p className="text-slate-600 dark:text-slate-300 mb-4">
            {isActive ? (
              <>Your session will expire in <span className="font-semibold tabular-nums text-red-600 dark:text-red-400">{formatTimeRemaining()}</span> due to inactivity.</>
            ) : (
              <>Your session is about to expire in <span className="font-semibold text-red-600 dark:text-red-400">less than 5 seconds</span>.</>
            )}
          </p>
          <p className="text-slate-600 dark:text-slate-300">
            Any unsaved changes will be lost.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
          <Button variant="secondary" onClick={onLogout}>
            Log out now
          </Button>
          <Button
            id="session-continue-button"
            variant="primary"
            onClick={onContinue}
            disabled={!isActive}
          >
            {isActive ? 'Continue session' : 'Logging out...'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutWarning;
